import React, { useEffect, useState } from "react";

type Props = {
  isOpen: boolean;
  isLoading?: boolean;
  onConfirm: () => Promise<boolean> | boolean;
  onClose: () => void;
};

export default function MetaAdsCapiTokenConfirmModal({
  isOpen,
  isLoading = false,
  onConfirm,
  onClose,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    // Prevent layout shift when scrollbar disappears
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const busy = isLoading || isSubmitting;

  return (
    <div className="fixed inset-0 z-[100010] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm overflow-y-auto">
      {/* Gradient Border Wrapper - same as meta-ads-submitting-modal */}
      <div
        className="relative w-full my-auto  lg:w-[716px] lg:h-[382px]  rounded-[30px] lg:rounded-[40px] min-w-0"
        style={{
          maxWidth: "800px",
          padding: "1px",
          background:
            "linear-gradient(180deg, rgba(130, 135, 152, 0) 0%, rgba(130, 135, 152, 0.4) 100%)",
        }}
      >
        {/* Actual Modal */}
        <div className="relative bg-[#0e0e10] lg:w-[716px] lg:h-[382px]  shadow-2xl overflow-hidden rounded-2xl sm:rounded-[28px] md:rounded-[40px] pt-10 pb-9 sm:pt-12 sm:pb-12 md:pt-16 md:pb-14">
          {/* Centered Top Gradient - same as meta-ads-submitting-modal */}
          <div
            className="absolute left-1/2 -translate-x-1/2 opacity-80 blur-2xl sm:blur-3xl w-[280px] h-[80px] sm:w-[360px] sm:h-[110px] md:w-[460px] md:h-[140px]"
            style={{
              top: "-60px",
              background:
                "linear-gradient(226.45deg, #1B76FF 12.8%, #2BFAA3 93.65%)",
            }}
          />

          <div className="relative z-10 px-4 sm:px-6 md:px-12 lg:px-[68px] lg:mt-[10px]">
            <h2 className="text-white lg:ml-[30px] text-center text-[24px] lg:w-[500px] lg:text-[32px] font-semibold leading-[130%]">
              Validation of CAPI Token for Selected Pixel
            </h2>

            <p className="lg:text-white/70 text-[#FFFFFF] text-center  font-normal mt-3 sm:mt-4 text-[14px] lg:text-[16px] leading-[150%]">
              Are you sure the CAPI token was created for the selected Pixel ID?
              <br />
              To avoid any issues, please confirm this before saving.
            </p>

            <div className="mt-7 sm:mt-9">
              <div className="w-full h-px bg-white/10" />
            </div>

            <div className="mt-6 mb-[20px] flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className={`lg:w-[167px] lg:h-[48px] w-full px-[32px] py-[12px] rounded-[999px] font-medium text-[16px] ${
                  busy
                    ? "bg-[#4B4F55] text-white/60 cursor-not-allowed"
                    : "bg-[#4B4F55] text-white"
                }`}
              >
                Check again
              </button>

              <button
                type="button"
                onClick={async () => {
                  if (busy) return;
                  setIsSubmitting(true);
                  try {
                    const ok = await onConfirm();
                    if (ok) onClose();
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={busy}
                className={`lg:w-[137px] lg:h-[48px] w-full px-[32px] py-[12px] rounded-[999px] font-medium text-[16px] ${
                  busy
                    ? "bg-white/70 text-[#020202] cursor-not-allowed"
                    : "bg-white text-[#020202]"
                }`}
              >
                <span className="inline-flex items-center justify-center text-[16px] font-medium gap-2">
                  {busy && (
                    <span className="inline-block w-4 h-4 rounded-full border-2 border-[#020202]/30 border-t-[#020202] animate-spin" />
                  )}
                  Confirm
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
